/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {history as defaultHistory, Route, Router} from '@exc/router';
import ExcCore, {getQueryValue, NotFound, Sandbox} from '@exc/core';
import MetricsRuntime from '@exc/metrics-runtime';
import PropTypes from 'prop-types';
import React from 'react';
import * as solutionConfigs from './solutions';
import './App.css';

export default function UnifiedShell({env, history, region, solutions, spaVersion}) {
  env = env.toLowerCase();
  history = history || defaultHistory;
  solutions = solutions || solutionConfigs;

  const {hash, pathname, search} = window.location;
  let localConfigs = JSON.parse(window.localStorage.getItem('unifiedShellConfig'));
  let devmodeEnabled = localConfigs && localConfigs.devmodeEnabled === true;
  if (getQueryValue('devmode') === 'true' || devmodeEnabled) {
    solutions = localConfigs ? {...solutions, ...localConfigs.solutions} : solutions;
  }

  MetricsRuntime.configure({
    analytics: {
      script: env === 'prod' ? '//assets.adobedtm.com/launch-ENc5d896b39e2d478ea2d22b79b1ae9e91.min.js'
        : '//assets.adobedtm.com/launch-ENecd21a85c13248a8832b233c41cf22d6-staging.min.js',
      solution: 'amc' // initial value
    },
    environment: env
  });
  window.adobeMetrics.setApplication({
    id: 'unified-shell',
    name: 'Experience Cloud Unified Shell',
    version: spaVersion || 'local.development'
  });

  // Sort the solutions by number of chunks in the path. This is required to
  // handle /home and /home/notifications. The latter needs to come first so
  // that it matches correctly in the router.
  solutions = Object.values(solutions).sort((a, b) => {
    const aLen = a.path.split('/').length;
    const bLen = b.path.split('/').length;
    if (aLen < bLen) {
      return 1;
    } else if (aLen > bLen) {
      return -1;
    }
    return 0;
  });

  // If there is a path defined, it's not valid in the Unified Shell URL. It
  // replaces the history path to `/` and updates the hash history (`history`)
  // to the provided path.
  if (history && /^(\/((?!login).){1,})$/.test(pathname)) {
    window.history.replaceState(null, null, '/');
    // The hash property defaults to `#/` since Unified Shell uses hash history.
    // Don't want this to transfer to the final path.
    history.replace({hash: hash === '#/' ? '' : hash, pathname, search});
  }

  return (
    <ExcCore env={env} region={region} spaVersion={spaVersion}>
      <Router history={history}>
        {/* Handle all solutions with optional tenant */}
        {Object.values(solutions).map((solution, idx) => (
          <Route key={idx} path={`(/@|):id?${solution.path}`}>
            <Sandbox config={solution} />
          </Route>
        ))}

        {/* Handle solution redirect options */}
        {[...Object.values(solutions).map((solution, idx) => (
          (solution.redirectFrom || []).map((redirect, redirectIdx) => (
            <Route exact key={`${idx}_${redirectIdx}`} path={`(/@|):id?${redirect}`} redirect={({route}) => {
              return route.replace(redirect, solution.path);
            }} />
          ))
        ))]}

        {/* Handle URLs that only have tenant */}
        <Route path="/@:id" redirect={({route}) => {
          return `${route}${route.endsWith('/') ? '' : '/'}home`;
        }} />

        {/* Handle no path */}
        <Route exact path="/" redirect="/home" />
        <Route>
          <NotFound />
        </Route>
      </Router>
    </ExcCore>
  );
}

UnifiedShell.defaultProps = {
  solutions: null
};

UnifiedShell.propTypes = {
  env: PropTypes.string.isRequired,
  region: PropTypes.string,
  solutions: PropTypes.object,
  spaVersion: PropTypes.string
};
